import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, User, AlertCircle } from 'lucide-react';
import { Customer, updateCustomer } from '../../lib/customers';
import { getCustomer } from '../../lib/customers';
import AddressAutocompleteModal from '../AddressAutocompleteModal';

interface EditCustomerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (customer: Customer) => void;
  onError?: (error: string) => void;
  customer: Customer | null;
}

export default function EditCustomerModal({
  isOpen,
  onClose,
  onSuccess,
  onError,
  customer
}: EditCustomerModalProps) {
  const [isAnimating, setIsAnimating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    type: 'person' as 'person' | 'business',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    address: ''
  });

  useEffect(() => {
    if (isOpen && customer) {
      setIsAnimating(true);
      setError(null);

      // Load fresh customer data
      const loadCustomer = async () => {
        try {
          if (!customer?.id) {
            throw new Error('Invalid customer ID');
          }

          const freshCustomer = await getCustomer(customer.id);

          setFormData({
            type: freshCustomer.type ?? 'person',
            first_name: freshCustomer.first_name ?? '',
            last_name: freshCustomer.last_name ?? '',
            email: freshCustomer.email ?? '',
            phone: freshCustomer.phone ?? '',
            address: freshCustomer.address ?? ''
          });
          setError(null);
        } catch (err) {
          console.error('Error loading customer:', err);
          const errorMessage = err instanceof Error ? err.message : 'Failed to load customer details';
          setError(errorMessage);
          if (onError) {
            onError(errorMessage);
          }
        }
      };
      loadCustomer();
    }
  }, [isOpen, customer]);

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
      setError(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!customer || !customer.id) {
      setError('Invalid customer ID');
      if (onError) {
        onError('Invalid customer ID');
      }
      return;
    }
    
    try {
      setLoading(true);
      setError(null);

      // Validate required fields
      if (!formData.first_name?.trim()) {
        throw new Error('First name is required');
      }
      if (!formData.last_name?.trim()) {
        throw new Error('Last name is required');
      }
      if (!formData.phone?.trim()) {
        throw new Error('Phone number is required');
      }

      const updatedCustomer = await updateCustomer(customer.id, formData);
      
      onSuccess(updatedCustomer);
    } catch (err) {
      console.error('Error updating customer:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to update customer details';
      setError(errorMessage);
      if (onError) {
        onError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen && !isAnimating) return null;
  if (!customer) return null;

  const modalContent = (
    <div 
      className={`fixed inset-0 bg-black transition-opacity duration-300 ${
        isOpen ? 'bg-opacity-50' : 'bg-opacity-0'
      } z-[100010]`}
      onClick={onClose}
      style={{
        zIndex: 100010,
        transform: 'translateZ(0)',
        WebkitTransform: 'translateZ(0)',
        isolation: 'isolate',
        touchAction: 'manipulation',
        WebkitTouchCallout: 'none',
        WebkitUserSelect: 'none',
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'env(safe-area-inset-bottom)',
        paddingLeft: 'env(safe-area-inset-left)',
        paddingRight: 'env(safe-area-inset-right)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
      }}
    >
      <div 
        className={`fixed inset-y-0 right-0 w-full md:w-[600px] bg-white transform transition-transform duration-300 ease-out shadow-2xl ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        onTransitionEnd={handleTransitionEnd}
        onClick={e => e.stopPropagation()}
      >
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="bg-black border-b border-gray-800">
            <div className="flex items-center justify-between px-6 py-4">
              <h2 className="text-xl font-semibold text-white">
                Edit Customer
              </h2>
              <button 
                onClick={onClose} 
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Form Content */}
          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto bg-gray-50">
            <div className="p-6 space-y-6">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                  <div className="flex items-center">
                    <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
                    {error}
                  </div>
                </div>
              )}

              {/* Customer Information */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Customer Information</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      First Name *
                    </label>
                    <input
                      type="text"
                      required
                      placeholder="Enter first name"
                      value={formData.first_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, first_name: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      required
                      placeholder="Enter last name"
                      value={formData.last_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, last_name: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="space-y-4 mt-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      required
                      placeholder="Enter phone number"
                      value={formData.phone}
                      onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      placeholder="Enter email address (optional)"
                      value={formData.email}
                      onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Address
                      <span className="text-xs text-gray-500 ml-1">Google autocomplete enabled</span>
                    </label>
                    <AddressAutocompleteModal
                      value={formData.address}
                      onChange={(address) => setFormData(prev => ({ ...prev, address }))}
                      onSelect={(address) => setFormData(prev => ({ ...prev, address: address.formatted }))}
                      placeholder="Enter address (optional)"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

            </div>

            {/* Submit Button */}
            <div className="p-6 bg-white border-t border-gray-200">
              <button
                type="submit"
                disabled={loading}
                className={`w-full px-4 py-3 text-white font-semibold rounded-lg transition-colors shadow-sm ${
                  loading ? 'bg-gray-300' : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Saving Changes...
                  </div>
                ) : (
                  'Save Changes'
                )}
              </button>
            </div>
                     </form>
         </div>
       </div>
     </div>
   );

  return createPortal(modalContent, document.body);
 }